import React, {useState, useEffect, useCallback} from 'react';
import axios from 'axios';
import BackOfficeMenu from '../components/BackOfficeMenu';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import {usePermissions} from '../../utils/usePermissions';
import './Installateur.css';



const BASE = () => {
    const url = import.meta.env.VITE_API_URL;
    return url.endsWith('/api') ? url : `${url}/api`;
};
const API = () => `${BASE()}/installateurs`;
const authHeader = () => ({headers: {Authorization: `Bearer ${localStorage.getItem('token')}`}});

const emptyForm = {
    prenom: '', nom: '', mail: '', numeroTelephone: '',
    adresse: '', region: '', ville: '', age: '',
    nbrClients: 0
};

const InstallateurManagement = ({onNavigate, onLogout, currentPage, isEmbedded = false}) => {
    const {hasPermission} = usePermissions();
    const [data, setData] = useState([]);
    const [stats, setStats] = useState({total: 0, totalNbrClients: 0, moyenneClients: 0, parCategorie: []});
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [msg, setMsg] = useState('');
    const [msgType, setMsgType] = useState('success');
    const [search, setSearch] = useState('');
    const [sortField, setSortField] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState('desc');

    /* ── Guard ──────────────────────────── */
    useEffect(() => {
        const userType = localStorage.getItem('typeUser');
        if (userType !== 'admin' && userType !== 'moderateur') {
            onNavigate('profile');
            return;
        }
        if (userType === 'moderateur' && !hasPermission('installateurs', 'read')) {
            onNavigate('dashboard');
            return;
        }
    }, [onNavigate, hasPermission]);

    /* ── Fetch list + stats ─────────────── */
    const fetchAll = useCallback(async () => {
        setLoading(true);
        try {
            const [listRes, statsRes] = await Promise.all([
                axios.get(API(), authHeader()),
                axios.get(`${API()}/stats`, authHeader()),
            ]);
            setData(Array.isArray(listRes.data) ? listRes.data : []);
            setStats(statsRes.data || {total: 0, totalNbrClients: 0, moyenneClients: 0});
        } catch (err) {
            if (err.response?.status === 401 || err.response?.status === 403) onLogout();
        } finally {
            setLoading(false);
        }
    }, [onLogout]);

    useEffect(() => {fetchAll();}, [fetchAll]);



    /* ── Helpers ────────────────────────── */
    const flash = (m, t = 'success') => {
        setMsg(m); setMsgType(t);
        setTimeout(() => setMsg(''), 4500);
    };

    const openModal = async (item = null) => {
        setEditing(item);
        setForm(item ? {
            prenom: item.prenom || '', nom: item.nom || '',
            mail: item.mail || '', numeroTelephone: item.numeroTelephone || '',
            adresse: item.adresse || '', region: item.region || '',
            ville: item.ville || '', age: item.age || '',
            nbrClients: item.nbrClients ?? 0
        } : emptyForm);

        setMsg('');
        setShowModal(true);
    };

    const closeModal = () => {setShowModal(false); setEditing(null); setMsg('');};

    const handleChange = (e) => {
        const {name, value} = e.target;
        setForm(prev => ({
            ...prev,
            [name]: name === 'nbrClients' || name === 'age' ? Number(value) : value,
        }));
    };



    /* ── CRUD ───────────────────────────── */
    const handleSubmit = async (e) => {
        e.preventDefault();
        setMsg('');
        const payload = form;
        try {
            if (editing) {
                await axios.put(`${API()}/${editing._id}`, payload, authHeader());
                flash('Chef d\'équipe modifié avec succès !');
            } else {
                await axios.post(API(), payload, authHeader());
                flash('Chef d\'équipe créé. Un email avec le mot de passe a été envoyé.');
            }
            await fetchAll();
            setTimeout(closeModal, 1400);
        } catch (err) {
            const m = err.response?.data?.message || 'Erreur lors de la sauvegarde.';
            setMsg(m); setMsgType('error');
        }
    };

    const handleDelete = async (id, prenom, nom) => {
        if (!window.confirm(`Supprimer le chef d'équipe ${prenom} ${nom} ?`)) return;
        try {
            await axios.delete(`${API()}/${id}`, authHeader());
            flash('Chef d\'équipe supprimé.');
            await fetchAll();
        } catch (err) {
            flash(err.response?.data?.message || 'Erreur lors de la suppression.', 'error');
        }
    };

    /* ── Sort ───────────────────────────── */
    const toggleSort = (f) => {
        if (sortField === f) setSortOrder(o => (o === 'asc' ? 'desc' : 'asc'));
        else {setSortField(f); setSortOrder('asc');}
    };
    const SortIcon = ({field}) => {
        if (sortField !== field) return <span className="sort-neutral">↕</span>;
        return <span className="sort-active">{sortOrder === 'asc' ? '↑' : '↓'}</span>;
    };

    /* ── Filtered + sorted list ─────────── */
    const filtered = [...data]
        .filter(i => {
            const q = search.toLowerCase();
            const matchSearch = !q ||
                i.nom?.toLowerCase().includes(q) ||
                i.prenom?.toLowerCase().includes(q) ||
                i.mail?.toLowerCase().includes(q) ||
                i.ville?.toLowerCase().includes(q);
            return matchSearch;
        })
        .sort((a, b) => {
            let av = a[sortField] ?? ''; let bv = b[sortField] ?? '';
            if (av < bv) return sortOrder === 'asc' ? -1 : 1;
            if (av > bv) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        });

    /* ── Render ─────────────────────────── */
    return (
        <div className={`inst-management ${isEmbedded ? 'embedded' : ''}`}>
            {!isEmbedded && <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />}

            <div className="inst-content" style={isEmbedded ? { marginLeft: 0, padding: 0 } : {}}>

                {/* Header */}
                <div className="inst-header">
                    <div className="inst-header-text">
                        <h1>🔧 Gestion des Chefs d'Équipe</h1>
                        <p>Liste des chefs d'équipe</p>
                    </div>
                    {hasPermission('installateurs', 'create') && (
                        <button className="inst-btn-add" onClick={() => openModal()}>
                            <i className="fas fa-plus"></i> Ajouter un chef d'équipe
                        </button>
                    )}
                </div>

                {/* KPI */}
                <div className="inst-kpi-row">
                    <div className="inst-kpi blue">
                        <div className="inst-kpi-icon">🔧</div>
                        <div><div className="inst-kpi-val">{stats.total}</div><div className="inst-kpi-lbl">Chefs d'Équipe</div></div>
                    </div>
                    <div className="inst-kpi green">
                        <div className="inst-kpi-icon">👥</div>
                        <div><div className="inst-kpi-val">{stats.totalNbrClients || 0}</div><div className="inst-kpi-lbl">Total Clients</div></div>
                    </div>
                    <div className="inst-kpi orange">
                        <div className="inst-kpi-icon">📊</div>
                        <div><div className="inst-kpi-val">{(stats.moyenneClients || 0).toFixed(1)}</div><div className="inst-kpi-lbl">Moy. Clients</div></div>
                    </div>
                </div>



                {/* Status */}
                {msg && !showModal && (
                    <div className={`inst-status ${msgType}`}>{msg}</div>
                )}

                {/* Toolbar */}
                <div className="inst-toolbar">
                    <div className="inst-search-box">
                        <span>🔍</span>
                        <input
                            type="text"
                            placeholder="Rechercher par nom, prénom, email, ville..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </div>
                    <span className="inst-count">{filtered.length} résultat{filtered.length !== 1 ? 's' : ''}</span>
                </div>

                {/* Table */}
                {loading ? (
                    <LoadingSpinner message="Chargement des chefs d'équipe..." />
                ) : (
                    <div className="inst-table-wrapper">
                        <table className="inst-table">
                            <thead>
                                <tr>
                                    <th className="sortable" onClick={() => toggleSort('prenom')}>Prénom <SortIcon field="prenom" /></th>
                                    <th className="sortable" onClick={() => toggleSort('nom')}>Nom <SortIcon field="nom" /></th>
                                    <th>Email</th>
                                    <th>Téléphone</th>
                                    <th className="sortable" onClick={() => toggleSort('nbrClients')}>Nbr Clients <SortIcon field="nbrClients" /></th>

                                    <th>Statut</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="inst-empty">
                                            <div className="inst-empty-icon">🔧</div>
                                            <p>Aucun chef d'équipe trouvé</p>
                                            <button className="inst-btn-primary" onClick={() => openModal()}>Ajouter le premier</button>
                                        </td>
                                    </tr>
                                ) : filtered.map(i => (
                                    <tr key={i._id}>
                                        <td>
                                            <div className="inst-name-cell">
                                                <div className="inst-avatar">
                                                    {i.prenom?.charAt(0).toUpperCase()}{i.nom?.charAt(0).toUpperCase()}
                                                </div>
                                                <span>{i.prenom}</span>
                                            </div>
                                        </td>
                                        <td><strong>{i.nom}</strong></td>
                                        <td><a href={`mailto:${i.mail}`} className="mail-link">{i.mail}</a></td>
                                        <td>{i.numeroTelephone || <span className="no-val">—</span>}</td>
                                        <td><span className="inst-clients-badge">{i.nbrClients ?? 0}</span></td>

                                        <td>
                                            <span className={`inst-status-badge ${i.isActive ? 'active' : 'inactive'}`}>
                                                {i.isActive ? 'Actif' : 'Inactif'}
                                            </span>
                                        </td>
                                        <td className="inst-actions">
                                            {hasPermission('installateurs', 'update') && (
                                                <button className="inst-btn-edit" onClick={() => openModal(i)}>✏️ Modifier</button>
                                            )}
                                            {hasPermission('installateurs', 'delete') && (
                                                <button className="inst-btn-delete" onClick={() => handleDelete(i._id, i.prenom, i.nom)}>🗑️ Supprimer</button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* ── Modal ─────────────────────────── */}
            {showModal && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="inst-modal" onClick={e => e.stopPropagation()}>
                        <div className="inst-modal-header">
                            <h2>{editing ? '✏️ Modifier le Chef d\'Équipe' : '➕ Nouveau Chef d\'Équipe'}</h2>
                            <button className="inst-modal-close" onClick={closeModal}>×</button>
                        </div>

                        {msg && (
                            <div className={`inst-status ${msgType}`} style={{margin: '0 24px 12px'}}>{msg}</div>
                        )}

                        <form onSubmit={handleSubmit} className="inst-form">

                            {/* Identité */}
                            <div className="inst-form-row">
                                <div className="inst-form-group">
                                    <label htmlFor="i-prenom">Prénom *</label>
                                    <input id="i-prenom" type="text" name="prenom" value={form.prenom} onChange={handleChange} placeholder="Ex: Mohamed" required />
                                </div>
                                <div className="inst-form-group">
                                    <label htmlFor="i-nom">Nom *</label>
                                    <input id="i-nom" type="text" name="nom" value={form.nom} onChange={handleChange} placeholder="Ex: Ben Salah" required />
                                </div>
                            </div>

                            <div className="inst-form-group">
                                <label htmlFor="i-mail">Email *</label>
                                <input id="i-mail" type="email" name="mail" value={form.mail} onChange={handleChange} placeholder="Ex: chef@sastra.tn" required />
                            </div>

                            <div className="inst-form-row">
                                <div className="inst-form-group">
                                    <label htmlFor="i-tel">Téléphone</label>
                                    <input id="i-tel" type="tel" name="numeroTelephone" value={form.numeroTelephone} onChange={handleChange} placeholder="Ex: 98 765 432" />
                                </div>
                                <div className="inst-form-group">
                                    <label htmlFor="i-age">Âge</label>
                                    <input id="i-age" type="number" name="age" value={form.age} onChange={handleChange} min={18} max={80} />
                                </div>
                            </div>

                            <div className="inst-form-row">
                                <div className="inst-form-group">
                                    <label htmlFor="i-ville">Ville</label>
                                    <input id="i-ville" type="text" name="ville" value={form.ville} onChange={handleChange} placeholder="Ex: Sfax" />
                                </div>
                                <div className="inst-form-group">
                                    <label htmlFor="i-region">Région</label>
                                    <input id="i-region" type="text" name="region" value={form.region} onChange={handleChange} />
                                </div>
                            </div>

                            <div className="inst-form-group">
                                <label htmlFor="i-adresse">Adresse</label>
                                <input id="i-adresse" type="text" name="adresse" value={form.adresse} onChange={handleChange} />
                            </div>

                            <div className="inst-form-separator">Informations Professionnelles</div>

                            <div className="inst-form-group">
                                <label htmlFor="i-nbrclients">Nombre de Clients</label>
                                <input id="i-nbrclients" type="number" name="nbrClients" value={form.nbrClients} onChange={handleChange} min={0} />
                            </div>



                            {!editing && (
                                <div className="inst-info-box">
                                    ℹ️ <strong>Mot de passe automatique :</strong> Un mot de passe sera généré et envoyé par email au chef d'équipe.
                                </div>
                            )}

                            <div className="inst-modal-actions">
                                <button type="button" className="inst-btn-secondary" onClick={closeModal}>Annuler</button>
                                <button type="submit" className="inst-btn-primary">{editing ? '💾 Modifier' : '➕ Créer'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InstallateurManagement;
